import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { secureAssetUrl } from "../../lib/secureUrl";
import RichText from "./RichText";
import Mermaid from "./Mermaid";
import Artifact from "./Artifact";
import BlocksRenderer from "./Blocks";
import { ChevronRight } from "lucide-react";

const HeadingText = ({ level, spans, className, dash = true }) => {
  const inner = (
    <>
      {dash && <span className="text-pillet-sage mr-2">—</span>}
      <RichText spans={spans} />
    </>
  );
  if (level === 1) return <h1 className={className}>{inner}</h1>;
  if (level === 2) return <h2 className={className}>{inner}</h2>;
  if (level === 3) return <h3 className={className}>{inner}</h3>;
  return <h4 className={className}>{inner}</h4>;
};

export const Heading = ({ node }) => {
  const sizes = {
    1: "text-3xl sm:text-4xl mt-10 mb-4",
    2: "text-2xl sm:text-3xl mt-9 mb-3",
    3: "text-xl sm:text-2xl mt-7 mb-2",
    4: "text-lg sm:text-xl mt-5 mb-2",
  };

  if (node.children && node.children.length) {
    return (
      <Accordion type="single" collapsible className="my-4">
        <AccordionItem value={node.id} className="border-b border-pillet-forest">
          <AccordionTrigger
            className="group py-3 hover:no-underline text-left data-[state=open]:text-pillet-sage [&>svg]:hidden"
            data-testid="toggle-heading"
          >
            <span className="flex items-center gap-2.5">
              <ChevronRight className="h-5 w-5 shrink-0 text-pillet-sage transition-transform duration-200 group-data-[state=open]:rotate-90" />
              <HeadingText
                level={node.level}
                spans={node.spans}
                dash={false}
                className={`font-heading font-bold text-white group-data-[state=open]:text-pillet-sage ${
                  node.level >= 4 ? "text-lg" : "text-xl sm:text-2xl"
                }`}
              />
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-4 pt-1 pl-7">
            <BlocksRenderer nodes={node.children} />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    );
  }

  return (
    <HeadingText
      level={node.level}
      spans={node.spans}
      className={`font-heading font-bold text-white ${sizes[node.level] || sizes[4]}`}
    />
  );
};

export const Callout = ({ node }) => (
  <div className="pillet-card pillet-hatch my-5 p-5 flex gap-4" data-testid="callout">
    {node.icon && <span className="text-2xl leading-none">{node.icon}</span>}
    <div className="text-white/90 leading-relaxed">
      <RichText spans={node.spans} />
      {node.children && <BlocksRenderer nodes={node.children} />}
    </div>
  </div>
);

export const Toggle = ({ node }) => (
  <Accordion type="single" collapsible className="my-3">
    <AccordionItem value={node.id} className="border border-pillet-forest">
      <AccordionTrigger
        className="group px-4 py-3 hover:no-underline hover:bg-pillet-forest/40 text-left font-heading text-white data-[state=open]:text-pillet-sage [&>svg]:hidden"
        data-testid="toggle-trigger"
      >
        <span className="flex items-center gap-2.5">
          <ChevronRight className="h-4 w-4 shrink-0 text-pillet-sage transition-transform duration-200 group-data-[state=open]:rotate-90" />
          <span><RichText spans={node.spans} /></span>
        </span>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4 pt-1 pl-11 text-white/85">
        {node.children ? <BlocksRenderer nodes={node.children} /> : null}
      </AccordionContent>
    </AccordionItem>
  </Accordion>
);

const TableCell = ({ cell, header, rowHeader }) => {
  const base = "border border-pillet-forest px-4 py-2.5 text-left align-top";
  if (header) {
    return (
      <th className={`${base} font-heading uppercase text-xs tracking-wider text-pillet-sage`}>
        <RichText spans={cell} />
      </th>
    );
  }
  return (
    <td className={`${base} ${rowHeader ? "font-semibold text-white" : "text-white/85"}`}>
      <RichText spans={cell} />
    </td>
  );
};

export const NotionTable = ({ node }) => (
  <div className="my-6 overflow-x-auto border border-pillet-sage" data-testid="notion-table">
    <table className="w-full border-collapse text-sm">
      <tbody>
        {node.rows.map((row, ri) => {
          const isHeader = node.has_column_header && ri === 0;
          return (
            <tr key={ri} className={isHeader ? "bg-pillet-forest" : ri % 2 ? "bg-pillet-card/40" : ""}>
              {row.map((cell, ci) => (
                <TableCell
                  key={ci}
                  cell={cell}
                  header={isHeader}
                  rowHeader={node.has_row_header && ci === 0}
                />
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export const Columns = ({ node }) => (
  <div
    className="my-6 grid gap-6"
    style={{ gridTemplateColumns: `repeat(${node.columns.length}, minmax(0, 1fr))` }}
  >
    {node.columns.map((col, i) => (
      <div key={i} className="min-w-0">
        <BlocksRenderer nodes={col} />
      </div>
    ))}
  </div>
);

export const ListItem = ({ item, marker }) => (
  <li className="flex gap-3 text-white/85 leading-relaxed">
    <span className="text-pillet-sage select-none mt-0.5 shrink-0">{marker}</span>
    <div className="min-w-0">
      <RichText spans={item.spans} />
      {item.children && <BlocksRenderer nodes={item.children} />}
    </div>
  </li>
);

export const Quote = ({ node }) => (
  <blockquote className="my-5 border-l-2 border-pillet-sage pl-4 italic text-white/80">
    <RichText spans={node.spans} />
  </blockquote>
);

export const Paragraph = ({ node }) => {
  if (!node.spans || node.spans.every((s) => !s.text)) return <div className="h-3" />;
  return (
    <p className="my-3 leading-relaxed text-white/85">
      <RichText spans={node.spans} />
    </p>
  );
};

export const CodeBlock = ({ node }) => (
  <pre className="pillet-card my-5 p-4 overflow-x-auto text-sm font-mono text-pillet-sage/90">
    <code>{node.code}</code>
  </pre>
);

export const ImageBlock = ({ node }) => {
  if (!node.url) return null;
  return (
    <figure className="my-6">
      <img src={secureAssetUrl(node.url)} alt="" className="w-full border border-pillet-forest" />
      {node.caption && node.caption.length ? (
        <figcaption className="text-pillet-muted text-xs mt-2">
          <RichText spans={node.caption} />
        </figcaption>
      ) : null}
    </figure>
  );
};

export { Mermaid, Artifact };
